import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { ErrorOutlineRounded, WarningAmberRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { documentCraftingFileUrls } from "../../../Api/documentCrafting";
import {
  DocumentCraftingDocumentListItem,
  DocumentCraftingRunBrief,
  DocumentCraftingRunStatus,
  isRunTerminal,
} from "../../../Models/documentCrafting";
import AuthImage from "../Common/AuthImage";
import {
  DocumentTypeIcon,
  DraftBadge,
  LanguageBadge,
  PersonalBadge,
  StatusChip,
  VisibilityIcon,
} from "../Common/DocumentCraftingUi";
import {
  documentCraftingPaths,
  useDocumentCraftingAccess,
} from "../Common/documentCraftingNavigation";
import { formatDate, formatDateTime, useUserName } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";

const isActive = (run: DocumentCraftingRunBrief | null | undefined) =>
  !!run && !isRunTerminal(run.status);

/** Where a row leads: a document still being created opens its progress
 * page (there is nothing to show yet), everything else the document. */
export const documentTarget = (item: DocumentCraftingDocumentListItem) =>
  !item.latest_revision && item.active_run && isActive(item.active_run)
    ? documentCraftingPaths.run(item.active_run.id)
    : documentCraftingPaths.document(item.id);

/** Clicks on links and buttons inside a clickable row handle themselves. */
export const isInteractiveTarget = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest("a, button, [role='button']");

const isDeck = (item: DocumentCraftingDocumentListItem) => item.type_key === "presentation";

/** First page of the latest saved revision, or the type icon. */
export const DocumentThumbnail: React.FC<{
  item: DocumentCraftingDocumentListItem;
  width?: number;
}> = ({ item, width = 72 }) => {
  const { t } = useTranslation("documentCrafting");
  const revision = item.latest_revision;
  const url =
    revision?.has_thumbnail
      ? documentCraftingFileUrls.revisionFile(item.id, revision.id, "thumbnail")
      : null;
  const deck = isDeck(item);
  const fallback = (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <DocumentTypeIcon
        typeKey={item.type_key}
        size={22}
        iconColor={color.gray400}
        tooltip={false}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width,
        height: Math.round(width * 0.86),
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AuthImage
        url={url}
        alt={t("list.row.thumbnail", { title: item.title })}
        aspectRatio={deck ? 16 / 9 : 210 / 297}
        fit="cover"
        fallback={fallback}
        sx={{
          width: deck ? "100%" : "62%",
          borderRadius: "4px",
          border: `1px solid ${color.gray200}`,
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
        }}
      />
    </Box>
  );
};

/** Type icon, type name and language badge. */
export const DocumentTypeLine: React.FC<{
  item: DocumentCraftingDocumentListItem;
  showName?: boolean;
}> = ({ item, showName = true }) => {
  const labels = useDocumentCraftingEnumLabels();
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
    >
      <DocumentTypeIcon typeKey={item.type_key} size={18} />
      {showName && (
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            color: color.gray700,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {labels.type(item.type_key)}
        </Typography>
      )}
      <LanguageBadge language={item.language} />
    </Box>
  );
};

/** Owner name (or "Deleted user"), the visibility icon and, for managers, the
 * hint that the owner left the workspace. Personal documents get the
 * "Personal document" badge when the list mixes them with workspace ones. */
export const DocumentOwner: React.FC<{ item: DocumentCraftingDocumentListItem }> = ({
  item,
}) => {
  const { t } = useTranslation("documentCrafting");
  const userName = useUserName();
  const { hasWorkspace } = useDocumentCraftingAccess();
  const ownerLeft =
    !!item.owner && !item.owner_in_workspace && item.permissions.can_manage;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <VisibilityIcon
          visibility={item.visibility}
          shared={item.share_count > 0}
          personal={item.is_personal}
          size={17}
        />
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            color: item.owner ? color.gray900 : color.gray500,
            fontStyle: item.owner ? "normal" : "italic",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {userName(item.owner)}
        </Typography>
      </Box>
      {item.is_personal && hasWorkspace && (
        <Box sx={{ mt: 0.5 }}>
          <PersonalBadge />
        </Box>
      )}
      {ownerLeft && (
        <Tooltip title={t("list.row.ownerLeftHint")} arrow>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
              color: color.orange600,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 14 }} />
            {t("list.row.ownerLeft")}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

/** "Revision N · date" and its author, or the state before revision 1. */
export const LatestRevision: React.FC<{ item: DocumentCraftingDocumentListItem }> = ({
  item,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const userName = useUserName();
  const revision = item.latest_revision;

  if (!revision) {
    return (
      <Typography sx={{ fontSize: 13, color: color.gray500 }}>
        {isActive(item.active_run)
          ? t("list.row.inCreation")
          : t("list.row.noRevision")}
      </Typography>
    );
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Tooltip title={formatDateTime(revision.saved_at, i18n.language)} arrow>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: color.gray900,
            whiteSpace: "nowrap",
          }}
        >
          {t("list.row.revision", {
            number: revision.number,
            date: formatDate(revision.saved_at, i18n.language),
          })}
        </Typography>
      </Tooltip>
      <Typography
        sx={{
          fontSize: 12,
          color: color.gray500,
          fontStyle: revision.saved_by ? "normal" : "italic",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {userName(revision.saved_by)}
      </Typography>
    </Box>
  );
};

/** The caller's running run: status and progress; opens the run page. */
export const ActiveRunChip: React.FC<{ run: DocumentCraftingRunBrief }> = ({
  run,
}) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const navigate = useNavigate();
  const awaiting = run.status === DocumentCraftingRunStatus.AWAITING_ANSWERS;
  const status = labels.runStatus(run.status);
  const step = run.step
    ? t(`run.steps.${run.step}`, { defaultValue: status })
    : status;
  const progress = Math.max(0, Math.min(100, Math.round(run.progress)));

  return (
    <Tooltip
      title={t(awaiting ? "list.row.runAwaitingHint" : "list.row.runHint", {
        kind: labels.runKind(run.kind),
        step,
      })}
      arrow
    >
      <Chip
        size="small"
        clickable
        onClick={() => navigate(documentCraftingPaths.run(run.id))}
        icon={
          awaiting ? (
            <ErrorOutlineRounded />
          ) : (
            <CircularProgress
              size={12}
              thickness={6}
              variant={progress > 0 ? "determinate" : "indeterminate"}
              value={progress}
            />
          )
        }
        label={
          awaiting
            ? status
            : t("list.row.runProgress", { status, progress })
        }
        sx={{
          height: 24,
          maxWidth: "100%",
          borderRadius: "12px",
          fontWeight: 700,
          backgroundColor: awaiting ? color.orange50 : color.blue50,
          color: awaiting ? color.orange600 : color.blue700,
          "&:hover": {
            backgroundColor: awaiting ? color.orange200 : color.blue200,
          },
          "& .MuiChip-label": { px: 1, fontSize: 12 },
          "& .MuiChip-icon": {
            ml: 0.75,
            mr: -0.25,
            fontSize: 15,
            color: "inherit",
          },
        }}
      />
    </Tooltip>
  );
};

/** Draft badge, running run and failed creation of one document. */
export const DocumentStatus: React.FC<{
  item: DocumentCraftingDocumentListItem;
  direction?: "row" | "column";
}> = ({ item, direction = "column" }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const run = isActive(item.active_run) ? item.active_run : null;
  if (!item.my_draft && !run && !item.creation_failed) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction,
        flexWrap: "wrap",
        alignItems: direction === "column" ? "flex-start" : "center",
        gap: 0.5,
      }}
    >
      {run && <ActiveRunChip run={run} />}
      {item.creation_failed && !run && (
        <Tooltip title={t("list.row.creationFailedHint")} arrow>
          <span>
            <StatusChip label={t("list.row.creationFailed")} tone="red" />
          </span>
        </Tooltip>
      )}
      {item.my_draft && (
        <Tooltip
          title={t("list.row.draftHint", {
            date: formatDateTime(item.my_draft.last_activity_at, i18n.language),
          })}
          arrow
        >
          <span>
            <DraftBadge />
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
